import React, { useEffect } from 'react';
import { WelcomePageLayout } from '@/components/cross-entity/onboarding';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, Vote, ShoppingCart, TrendingUp } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pricingAnalytics } from '@/utils/entities/clients/pricingTracking';

export const SilverWelcome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Ensure page starts at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContinue = () => {
    // Track analytics
    pricingAnalytics.trackTierSelection('silver', 'silver_welcome_page');

    // Preserve add-ons from URL and navigate to assessment
    const addonsParam = searchParams.get('addons');
    const assessmentUrl = addonsParam
      ? `/onboarding/assessment?tier=silver&addons=${encodeURIComponent(addonsParam)}`
      : '/onboarding/assessment?tier=silver';

    // Use direct navigation for reliability
    window.location.href = assessmentUrl;
  };

  return (
    <WelcomePageLayout tier="silver" onContinue={handleContinue}>
      <div className="space-y-8">
        {/* What's Next Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">What Happens Next?</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We'll start by understanding your community's unique needs, then create a custom
            implementation plan that transforms your HOA with digital governance and financial
            tools.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 font-bold text-xl">1</span>
              </div>
              <CardTitle className="text-lg">Community Assessment</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Tell us about your community structure, challenges, and goals
              </p>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-green-600 font-bold text-xl">2</span>
              </div>
              <CardTitle className="text-lg">Discovery Call</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Personalized consultation with our community transformation specialists
              </p>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-purple-600 font-bold text-xl">3</span>
              </div>
              <CardTitle className="text-lg">Custom Implementation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Tailored rollout plan designed specifically for your community
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Silver Package Highlights */}
        <div className="bg-linear-to-r from-gray-50 to-blue-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Your Silver Package Transformation Includes:
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 rounded-full p-3">
                <Vote className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Revolutionary Voting System</h4>
                <p className="text-sm text-gray-600">
                  Settle disputes with voting that leaves a receipt every resident can check and a
                  tally anyone can recount
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-green-100 rounded-full p-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Treasury Transparency</h4>
                <p className="text-sm text-gray-600">
                  Track HOA treasury transparently and coordinate group purchasing for potential
                  savings
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-purple-100 rounded-full p-3">
                <ShoppingCart className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Group Buying Power</h4>
                <p className="text-sm text-gray-600">
                  Potential savings of up to 25-50% on services through coordinated purchasing and
                  vendor negotiations
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-orange-100 rounded-full p-3">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Treasury Transparency</h4>
                <p className="text-sm text-gray-600">
                  Clear, up-to-date visibility into community funds so the board and residents see
                  the same numbers
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Expectations */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">What to Expect:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>
              • <strong>Assessment:</strong> 5-10 minutes to tell us about your community
            </li>
            <li>
              • <strong>Response Time:</strong> We'll contact you within 2 business hours
            </li>
            <li>
              • <strong>Discovery Call:</strong> 45-60 minute personalized consultation
            </li>
            <li>
              • <strong>Implementation:</strong> 6-8 week transformation timeline
            </li>
            <li>
              • <strong>Support:</strong> Dedicated community success team throughout the process
            </li>
          </ul>
        </div>
      </div>
    </WelcomePageLayout>
  );
};
